<template>
    <!-- the top five of one stat: a compact leaderboard, the leader on top -->
    <article class="stat-card">
        <header class="stat-card__head">
            <span class="stat-card__code">{{ statHeader.text }}</span>
            <span class="stat-card__label">{{ label }}</span>
            <button type="button" class="stat-card__more" title="Όλη η λίστα" @click="$emit('scrollToStats')">
                Όλοι<v-icon x-small>mdi-arrow-down</v-icon>
            </button>
        </header>
        <v-progress-linear v-if="loading" color="teal" indeterminate height="2" />

        <div v-if="!loading && !(stats && stats.length)" class="stat-card__empty">Δεν υπάρχουν ακόμη στοιχεία</div>
        <ol v-else class="stat-card__list">
            <li
                v-for="(item, index) in stats || []"
                :key="item.id || index"
                class="stat-card__row"
                :class="{ 'stat-card__row--leader': index === 0 }"
                @click="handleClick(item)"
            >
                <span class="stat-card__rank">{{ index + 1 }}</span>
                <template v-if="option">
                    <span class="cl-logo stat-card__logo stat-card__logo--team">
                        <img :src="`${assetsUrl}/${item.logo}`" alt="">
                    </span>
                    <span class="stat-card__who">
                        <span class="stat-card__name">{{ item.name }}</span>
                        <span class="stat-card__sub">{{ item.stats.length }} αγώνες</span>
                    </span>
                </template>
                <template v-else>
                    <img :src="`${assetsUrl}/${item.image}`" alt="" class="stat-card__photo" @error="$event.target.src = defaultPlayerImg">
                    <span class="stat-card__who">
                        <span class="stat-card__name">{{ item.name }}</span>
                        <span class="stat-card__sub">
                            <span class="cl-logo stat-card__logo">
                                <img :src="`${assetsUrl}/${item.teamLogo}`" alt="">
                            </span>
                            {{ item.team }} · {{ item.stats.length }} αγ.
                        </span>
                    </span>
                </template>
                <span class="stat-card__value">
                    <span class="stat-card__avg">{{ item.avg[statHeader.value].toFixed(1) }}</span>
                    <span class="stat-card__total">{{ item.cum[statHeader.value] }} σύνολο</span>
                </span>
            </li>
        </ol>
    </article>
</template>

<script>
    // the name of every stat, under its short code
    const LABELS = {
        points: 'Πόντοι',
        twoPointsMade: 'Εύστοχα δίποντα',
        threePointsMade: 'Εύστοχα τρίποντα',
        freeThrowsMade: 'Εύστοχες βολές',
        offensiveRebounds: 'Επιθετικά ριμπάουντ',
        defensiveRebounds: 'Αμυντικά ριμπάουντ',
        rebounds: 'Ριμπάουντ',
        assists: 'Ασίστ',
        steals: 'Κλεψίματα',
        turnovers: 'Λάθη',
        blockedShots: 'Τάπες',
        personalFouls: 'Φάουλ'
    }

    export default {
        props: {
            option: {
                type: Boolean,
                default: true
            },
            stats: {
                type: Array,
                default: null
            },
            statHeader: {
                type: Object,
                default: null
            },
            loading: {
                type: Boolean,
                default: true
            }
        },
        data() {
            return {
                defaultPlayerImg: require('@/assets/default_player.png'),
                assetsUrl: 'https://storage.googleapis.com'
            }
        },
        computed: {
            label() {
                return LABELS[this.statHeader.value] || ''
            }
        },
        methods: {
            handleClick(row) {
                if (!row.id) return
                this.$router.push({ path: this.option ? `/Team/${row.id}` : `/Player/${row.id}` })
            }
        }
    }
</script>

<style scoped>
.stat-card {
    display: flex;
    flex-direction: column;
    border-radius: 12px;
    background: #232323;
    color: #fff;
    box-shadow: 0 6px 20px rgba(35, 35, 35, 0.12);
    font-family: Roboto, Arial, sans-serif;
    text-align: left;
    overflow: hidden;
}

.stat-card__head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    padding: 14px 16px 12px;
    border-bottom: 2px solid rgba(3, 229, 183, 0.4);
}

.stat-card__code {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.5rem;
    line-height: 1;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #03e5b7;
}

.stat-card__label {
    flex: 1 1 auto;
    min-width: 0;
    font-size: 0.78rem;
    color: rgba(255, 255, 255, 0.6);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.stat-card__more {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    padding: 2px 8px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.72rem;
    font-weight: 700;
}

.stat-card__more .v-icon {
    color: inherit !important;
}

.stat-card__more:hover {
    border-color: #03e5b7;
    color: #03e5b7;
}

.stat-card__list {
    margin: 0;
    padding: 6px 0;
    list-style: none;
}

.stat-card__row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 16px;
    cursor: pointer;
    transition: background 0.15s;
}

.stat-card__row + .stat-card__row {
    border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.stat-card__row:hover {
    background: rgba(3, 229, 183, 0.07);
}

/* the leader of the stat */
.stat-card__row--leader {
    background: linear-gradient(90deg, rgba(3, 229, 183, 0.14) 0%, rgba(3, 122, 222, 0.06) 100%);
}

.stat-card__rank {
    flex: 0 0 22px;
    font-size: 0.85rem;
    font-weight: 800;
    color: rgba(255, 255, 255, 0.45);
    text-align: center;
}

.stat-card__row--leader .stat-card__rank {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    border-radius: 6px;
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    color: #fff;
}

.stat-card__photo {
    flex: 0 0 auto;
    width: 38px;
    height: 38px;
    border-radius: 50%;
    background: #2e2e2e;
    object-fit: cover;
    object-position: top;
}

.stat-card__logo {
    width: 18px;
    height: 18px;
    border-radius: 4px;
}

.stat-card__logo--team {
    width: 38px;
    height: 38px;
    border-radius: 8px;
}

.stat-card__who {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
}

.stat-card__name {
    font-size: 0.9rem;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.stat-card__row:hover .stat-card__name {
    color: #03e5b7;
}

.stat-card__sub {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    font-size: 0.72rem;
    color: rgba(255, 255, 255, 0.55);
    overflow: hidden;
    white-space: nowrap;
}

.stat-card__value {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex: 0 0 auto;
}

.stat-card__avg {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.6rem;
    line-height: 1;
    color: #fff;
}

.stat-card__row--leader .stat-card__avg {
    color: #03e5b7;
}

.stat-card__total {
    font-size: 0.68rem;
    color: rgba(255, 255, 255, 0.5);
}

.stat-card__empty {
    padding: 32px 16px;
    color: rgba(255, 255, 255, 0.5);
    text-align: center;
}
</style>
